/**
 * Notification Types and Interfaces
 * Complete type definitions for the UIC notification system
 */

/**
 * Notification variant determines the visual style and severity level
 */
export type NotificationVariant = 'alert' | 'warning' | 'info' | 'success';

/**
 * Severity rank for notification prioritisation
 */
export const NOTIFICATION_SEVERITY_RANK: Record<NotificationVariant, number> = {
  alert: 4,
  warning: 3,
  info: 2,
  success: 1,
};

/**
 * HTML field structure from Drupal
 */
export interface NotificationHtmlField {
  value?: string;
  processed?: string;
}

/**
 * Notified user entry stored in field_notified_users
 */
export interface NotifiedUserEntry {
  email: string;
  at: string;
  readable: string;
  [key: string]: unknown;
}

/**
 * Raw notification data structure from Drupal
 * Uses Record<string, unknown> for flexible Drupal data structure
 */
export interface DrupalNotification {
  id: string;
  title?: string;
  field_html?: NotificationHtmlField | { value: string };
  field_variant?: string;
  field_start?: string;
  field_end?: string;
  field_notified_users?: unknown;
  field_notif_memberships?: unknown;
  field_assembly?: unknown;
  field_applications?: unknown;
  created?: string;
  changed?: string;
  status?: string | number | boolean;
  attributes?: {
    field_html?: NotificationHtmlField;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Processed notification ready for display
 */
export interface ProcessedNotification {
  id: string;
  title: string;
  htmlContent: string;
  variant: NotificationVariant;
  startDate: Date | null;
  endDate: Date | null;
  isRead: boolean;
  createdAt: Date | null;
}

/**
 * Token replacement variables for personalisation
 */
export interface NotificationTokens {
  '[contact:first_name]'?: string;
  '[contact:last_name]'?: string;
  '[contact:full_name]'?: string;
  '[contact:email]'?: string;
  '[company:name]'?: string;
  '[company:code]'?: string;
  '[company:membership_label]'?: string;
  [key: string]: string | undefined;
}

/**
 * User context for notification filtering
 */
export interface NotificationUserContext {
  email?: string | null;
  membershipTypeId?: string | null;
  assemblyIds?: string[];
  companyCode?: string | null;
  identity?: Record<string, unknown>;
  membership?: Record<string, unknown>;
}

/**
 * Notification query parameters for Drupal API
 */
export interface NotificationQueryParams {
  'fields[node--notifications]'?: string;
  'fields[taxonomy_term--membership_type]'?: string;
  'fields[taxonomy_term--groups]'?: string;
  include?: string;
  'filter[status]'?: string;
  sort?: string;
  'page[limit]'?: string;
  [key: string]: string | undefined;
}

/**
 * Hook return value for notifications
 */
export interface UseNotificationsResult {
  items: DrupalNotification[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook return value for membership type ID
 */
export interface UseMembershipTypeIdResult {
  id: string | null;
  isLoading: boolean;
}

/**
 * App version update information
 */
export interface AppVersionInfo {
  hasUpdate: boolean;
  currentVersion: string | null;
  reload: () => void;
}

/**
 * API request body for marking notification as read
 */
export interface MarkNotificationReadRequest {
  appendNotified: boolean;
  entry: NotifiedUserEntry;
}

/**
 * Props for HeaderNotifications component
 */
export interface HeaderNotificationsProps {
  /** User email for filtering notifications */
  userEmail?: string | null;
  /** User context data for token replacement */
  userContext?: Record<string, unknown>;
  /** Maximum number of notifications to display */
  maxNotifications?: number;
}

/**
 * Props for NotificationsCard component
 */
export interface NotificationsCardProps {
  /** User context for filtering and token replacement */
  userContext?: NotificationUserContext;
  /** Maximum number of notifications to display */
  maxNotifications?: number;
  /** Custom CSS classes */
  className?: string;
}

/**
 * Props for UpdateNotification component
 */
export interface UpdateNotificationProps {
  /** Check interval in milliseconds (default: 5 minutes) */
  checkInterval?: number;
  /** Auto-dismiss after X milliseconds (0 = no auto-dismiss) */
  autoDismissAfter?: number;
}

